/**
 * Logger estruturado — superset de `apps/crm/src/lib/logger.ts` + `apps/public/src/lib/logger.ts`.
 *
 * Decisão B: console wrapper estruturado, zero deps. Justificativa:
 * - Pino/winston trazem 100KB+ de bundle e dependem de Node APIs (process, streams)
 *   que exigem polyfills em Vite/browser.
 * - Console preserva DevTools (object-inspector, source linking, expandable).
 * - Em prod, eventos relevantes vão para Sentry via captureException — logger é
 *   debugging local + buffer in-memory.
 */
import type {
  InitLoggerConfig,
  LogContext,
  LogEntry,
  Logger,
  LogLevel,
} from "./types";

const LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "Unknown error";
}

class LoggerImpl implements Logger {
  private readonly app: string;
  private readonly level: LogLevel;
  private readonly maxHistorySize: number;
  private readonly alwaysConsole: boolean;
  private readonly isDev: boolean;
  private history: LogEntry[] = [];

  constructor(config: InitLoggerConfig) {
    this.app = config.app;
    this.maxHistorySize = config.maxHistorySize ?? 100;
    this.alwaysConsole = config.alwaysConsole ?? false;
    this.isDev =
      typeof import.meta !== "undefined" &&
      typeof import.meta.env !== "undefined" &&
      import.meta.env.DEV === true;
    this.level = config.level ?? (this.isDev ? "debug" : "info");
  }

  private formatMessage(level: LogLevel, message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${this.app}] [${level.toUpperCase()}] ${message}`;
  }

  private addToHistory(entry: LogEntry): void {
    this.history.push(entry);
    if (this.history.length > this.maxHistorySize) this.history.shift();
  }

  private write(level: LogLevel, message: string, context?: LogContext): void {
    if (LEVEL_PRIORITY[level] < LEVEL_PRIORITY[this.level]) return;

    const entry: LogEntry = {
      level,
      message,
      context,
      timestamp: new Date().toISOString(),
      source: this.app,
    };
    this.addToHistory(entry);

    if (!this.isDev && !this.alwaysConsole) return;

    const formatted = this.formatMessage(level, message);
    const ctx = context ?? "";
    switch (level) {
      case "error":
        // eslint-disable-next-line no-console
        console.error(formatted, ctx);
        break;
      case "warn":
        // eslint-disable-next-line no-console
        console.warn(formatted, ctx);
        break;
      case "debug":
        // eslint-disable-next-line no-console
        console.debug(formatted, ctx);
        break;
      default:
        // eslint-disable-next-line no-console
        console.log(formatted, ctx);
    }
  }

  debug(message: string, context?: LogContext): void {
    this.write("debug", message, context);
  }
  info(message: string, context?: LogContext): void {
    this.write("info", message, context);
  }
  warn(message: string, context?: LogContext): void {
    this.write("warn", message, context);
  }
  error(message: string, context?: LogContext): void {
    this.write("error", message, context);
  }

  logError(error: Error, context?: LogContext): void {
    this.write("error", error.message, {
      ...context,
      stack: error.stack,
      name: error.name,
    });
  }

  createErrorId(): string {
    return `err_${Date.now().toString(36)}_${Math.random()
      .toString(36)
      .slice(2, 8)}`;
  }

  errorWithId(message: string, error?: unknown, context?: LogContext): string {
    const errorId = this.createErrorId();
    this.write("error", `${message} (${errorId})`, {
      errorId,
      ...context,
      errorMessage: getErrorMessage(error),
      ...(error instanceof Error
        ? { errorName: error.name, stack: error.stack }
        : {}),
    });
    return errorId;
  }

  getHistory(level?: LogLevel): LogEntry[] {
    if (level) return this.history.filter((e) => e.level === level);
    return [...this.history];
  }

  clearHistory(): void {
    this.history = [];
  }
}

export function initLogger(config: InitLoggerConfig): Logger {
  return new LoggerImpl(config);
}
